import React, { useCallback, type ReactElement } from 'react';
import { graphql, useFragment } from 'react-relay';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import { SECTION_ITEM_ROADMAPS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_ROADMAPS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import {
	L1_MENU_ID,
	type Nav4DragAndDropMenuItemProps,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { getTestId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useSectionItemAnalytics } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-section-item-analytics/index.tsx';
import { useConfigurableMessageAndIconMap } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component';
import { RoadmapsContent } from './RoadmapsContent.tsx';
import { PACKAGE_NAME, TEAM_NAME } from './common/constants.tsx';
import { Footer } from './common/ui/footer/index.tsx';
import type { RoadmapsContent_navigationCustomisation$key } from './__generated__/RoadmapsContent_navigationCustomisation.graphql.ts';
import type {
	RoadmapNavExpanded_query$key,
	RoadmapNavExpanded_query$data,
} from './__generated__/RoadmapNavExpanded_query.graphql.ts';

type RoadmapNavExpandedProps = Nav4DragAndDropMenuItemProps & {
	actionsOnHover: ReactElement;
	isExpanded: boolean;
	queryRefFragment: RoadmapNavExpanded_query$key;
};

export function RoadmapNavExpanded(props: RoadmapNavExpandedProps): React.JSX.Element {
	const { queryRefFragment } = props;
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.ROADMAPS);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();

	const dataRoadmapsQuery = useFragment<RoadmapNavExpanded_query$key>(
		graphql`
			fragment RoadmapNavExpanded_query on Query
			@argumentDefinitions(
				currentURL: { type: "URL" }
				isJpdRoadmapsRecentsEnabled: {
					type: "Boolean!"
					provider: "@jira/platform__graphql-relay-provider/src/isJpdRoadmapsRecentsEnabled.tsx"
				}
			) {
				...RoadmapsContent @arguments(currentURL: $currentURL)
				roadmapsOrder: settings_navigationCustomisation(
					entityAri: $entityAriRoadmaps
					ownerAri: $ownerAri
				) @include(if: $isJpdRoadmapsRecentsEnabled) {
					...RoadmapsContent_navigationCustomisation
				}
			}
		`,
		queryRefFragment,
	);

	const handleExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				sendSectionItemAnalyticsEvent({
					menuId: L1_MENU_ID.ROADMAPS,
					sectionItem: SECTION_ITEM_ROADMAPS,
				});
			}
		},
		[sendSectionItemAnalyticsEvent],
	);

	return (
		<RoadmapNavExpandedInner
			{...props}
			formattedMessage={formattedMessage}
			handleExpansionToggle={handleExpansionToggle}
			icon={icon}
			menuId={MENU_ID_ROADMAPS}
			navigationCustomisationFragment={dataRoadmapsQuery.roadmapsOrder ?? undefined}
			queryRef={dataRoadmapsQuery}
		/>
	);
}

const RoadmapNavExpandedInner = componentWithFG(
	'navx-global-side-nav',
	RoadmapNavExpandedInnerGSN,
	RoadmapNavExpandedInnerNav4,
);

type InnerProps = Omit<RoadmapNavExpandedProps, 'queryRef'> & {
	formattedMessage: string;
	handleExpansionToggle: (hasExpanded: boolean) => void;
	icon: JSX.Element;
	menuId: string;
	navigationCustomisationFragment?: RoadmapsContent_navigationCustomisation$key;
	queryRef: RoadmapNavExpanded_query$data;
};

function RoadmapNavExpandedInnerGSN(props: InnerProps) {
	const {
		actionsOnHover,
		draggableRef,
		dropIndicator,
		formattedMessage,
		handleExpansionToggle,
		hasDragIndicator,
		icon,
		isDragging,
		isExpanded,
		menuId,
		navigationCustomisationFragment,
		onExpandFlyoutToggle,
		queryRef,
		visualContentRef,
	} = props;

	const handleExpansionToggle2 = useCallback(
		(hasExpanded: boolean) => {
			handleExpansionToggle(hasExpanded);
			onExpandFlyoutToggle?.(hasExpanded);
		},
		[onExpandFlyoutToggle, handleExpansionToggle],
	);

	return (
		<Nav4ExpandableMenuItem
			dropTargetRef={visualContentRef}
			dropIndicator={dropIndicator}
			isDragging={isDragging}
			isExpanded={isExpanded}
			menuId={menuId}
			onExpansionToggle={handleExpansionToggle2}
		>
			<ExpandableMenuItemTrigger
				actionsOnHover={actionsOnHover}
				aria-label={formattedMessage}
				elemBefore={icon}
				hasDragIndicator={hasDragIndicator}
				interactionName="nav4-sidebar-roadmaps-landing"
				isDragging={isDragging}
				ref={draggableRef}
				testId={getTestId(menuId)}
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<UFOSegment name="nav4-sidebar-roadmaps-landing">
					<JSErrorBoundary
						id="nav4-sidebar-roadmaps-landing"
						packageName={PACKAGE_NAME}
						teamName={TEAM_NAME}
						fallback="flag"
					>
						<RoadmapsContent
							queryRef={queryRef}
							navigationCustomisationFragment={navigationCustomisationFragment}
						/>
						<Footer />
					</JSErrorBoundary>
				</UFOSegment>
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}

function RoadmapNavExpandedInnerNav4(props: InnerProps) {
	const {
		actionsOnHover,
		formattedMessage,
		handleExpansionToggle,
		icon,
		isExpanded,
		menuId,
		navigationCustomisationFragment,
		queryRef,
	} = props;

	return (
		<Nav4ExpandableMenuItem
			isExpanded={isExpanded}
			menuId={menuId}
			onExpansionToggle={handleExpansionToggle}
		>
			<ExpandableMenuItemTrigger
				actionsOnHover={actionsOnHover}
				// Prevent double-up of accessible name that occurs in icon + text situation.
				aria-label={formattedMessage}
				elemBefore={icon}
				testId={getTestId(menuId)}
				interactionName="nav4-sidebar-roadmaps-landing"
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<UFOSegment name="nav4-sidebar-roadmaps-landing">
					<JSErrorBoundary
						id="nav4-sidebar-roadmaps-landing"
						packageName={PACKAGE_NAME}
						teamName={TEAM_NAME}
						fallback="flag"
					>
						<RoadmapsContent
							queryRef={queryRef}
							navigationCustomisationFragment={navigationCustomisationFragment}
						/>
						<Footer />
					</JSErrorBoundary>
				</UFOSegment>
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}
